import type { AgencyIntent, CustomerPhotoMode, Generation } from '../types/generation';

type RecordLike = Record<string, unknown>;

const INTENT_LABELS: Record<AgencyIntent, string> = {
  catalog_clean: 'Карточка товара',
  studio_model: 'Фото на модели',
  customer_tryon: 'Примерка клиента',
  lifestyle_shoot: 'Lifestyle-сессия',
  marketing_design: 'Дизайн для соцсетей',
};

const PHOTO_MODE_LABELS: Record<CustomerPhotoMode, string> = {
  tryon_original: 'Фото клиента без ретуши',
  tryon_enhanced: 'Фото клиента с мягким улучшением',
};

function asRecord(value: unknown): RecordLike | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordLike)
    : undefined;
}

function asText(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
}

function asIntent(value: unknown): AgencyIntent | undefined {
  return value === 'catalog_clean' ||
    value === 'studio_model' ||
    value === 'customer_tryon' ||
    value === 'lifestyle_shoot' ||
    value === 'marketing_design'
    ? value
    : undefined;
}

function asPhotoMode(value: unknown): CustomerPhotoMode | undefined {
  return value === 'tryon_original' || value === 'tryon_enhanced' ? value : undefined;
}

function fallbackIntent(type: Generation['type']): AgencyIntent {
  if (type === 'catalog_clean') return 'catalog_clean';
  if (type === 'user_tryon') return 'customer_tryon';
  return 'studio_model';
}

export function getGenerationSummary(generation: Generation) {
  const metadata = asRecord(generation.metadata) ?? {};
  const agency = asRecord(metadata.agency);
  const route = asRecord(agency?.route) ?? asRecord(metadata.providerRoute);
  const params = asRecord(route?.params);
  const canvas = asRecord(agency?.canvas);
  const inputs = asRecord(agency?.inputs);
  const intent = asIntent(agency?.intent) ?? asIntent(metadata.intent) ?? fallbackIntent(generation.type);
  const customerPhotoMode = asPhotoMode(inputs?.customerPhoto) ?? asPhotoMode(metadata.customerPhotoMode);

  const model = asText(route?.model) ?? asText(metadata.fashnModel);
  const provider = asText(route?.provider) ?? (asText(metadata.fashnModel) ? 'fashn' : undefined);
  const capability = asText(route?.capability);
  const qualityMode = asText(agency?.qualityMode);
  const aspectRatio = asText(canvas?.aspectRatio) ?? asText(metadata.aspectRatio) ?? asText(params?.aspectRatio);
  const resolution = asText(params?.resolution) ?? asText(metadata.resolution);
  const generationMode = asText(params?.generationMode) ?? asText(metadata.generationMode);
  const outputFormat = asText(params?.outputFormat) ?? asText(metadata.outputFormat);
  const rawQaRuleIds = agency?.qaRuleIds;
  const qaRuleIds = Array.isArray(rawQaRuleIds)
    ? rawQaRuleIds.filter((id): id is string => typeof id === 'string')
    : [];

  const routeLabel = provider && model
    ? `${provider.toUpperCase()} ${model}`
    : model ?? (provider ? provider.toUpperCase() : undefined);

  const routeParams = [
    aspectRatio ? `канва ${aspectRatio}` : undefined,
    resolution ? `resolution ${resolution}` : undefined,
    generationMode ? `mode ${generationMode}` : undefined,
    outputFormat ? outputFormat.toUpperCase() : undefined,
  ].filter(Boolean) as string[];

  const chips = [
    routeLabel,
    customerPhotoMode ? PHOTO_MODE_LABELS[customerPhotoMode] : undefined,
    ...routeParams.slice(0, 2),
  ].filter(Boolean) as string[];

  return {
    intent,
    modeLabel: INTENT_LABELS[intent],
    customerPhotoMode,
    photoModeLabel: customerPhotoMode ? PHOTO_MODE_LABELS[customerPhotoMode] : undefined,
    provider,
    model,
    capability,
    routeLabel,
    qualityMode,
    aspectRatio,
    resolution,
    generationMode,
    outputFormat,
    qaRuleIds,
    routeParams,
    chips,
  };
}
